/**
 * @file 対応づけで自分を指している資料の問 (scan_items) をまとめて取得する hook。
 *
 * @what: 資料 id の並びを受け、id ごとに問の一覧を読んで Map にする。
 * @why: ページで読んでいるとき「このページに関する問題」を出すには、自分を相手に指定した
 *   資料 (過去問・塾技) の問が要る。資料を開いたときだけ取る。
 * @failure: 取得失敗は console に出し、その資料は空にする (読む操作は止めない)。
 */

import { useEffect, useState } from "react";
import { fetchScanItems, type StudyScanItem } from "../api/scans";

const EMPTY: ReadonlyMap<string, StudyScanItem[]> = new Map();

export function useRelatedItems(
  familyId: string,
  sourceIds: readonly string[],
): ReadonlyMap<string, StudyScanItem[]> {
  const [map, setMap] = useState<ReadonlyMap<string, StudyScanItem[]>>(EMPTY);
  const key = sourceIds.join("\n");

  useEffect(() => {
    const ids = key === "" ? [] : key.split("\n");
    setMap(EMPTY);
    if (ids.length === 0) return;
    const controller = new AbortController();
    Promise.all(
      ids.map((id) =>
        fetchScanItems(familyId, id, controller.signal).catch((err: unknown) => {
          if (!controller.signal.aborted)
            console.error("[study] failed to load related items", err);
          return [] as StudyScanItem[];
        }),
      ),
    ).then((lists) => {
      if (controller.signal.aborted) return;
      setMap(new Map(ids.map((id, i) => [id, lists[i] ?? []])));
    });
    return () => controller.abort();
  }, [familyId, key]);

  return map;
}
